import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { server } from "../../server";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
} from "../../utils/imageUploadPreparation";

const LifeEvents = ({ ownerId, profileType, canCreate = true }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [eventDetails, setEventDetails] = useState({ date: "", description: "" });
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [lifeEvents, setLifeEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPreparingBackground, setIsPreparingBackground] = useState(false);
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  const lifeEventOptions = [
    "Birthday",
    "Celebrations",
    "Marriage/Engagement",
    "Graduation",
    "Anniversary",
    "New Job",
    "New Home",
    "Retirement",
    "Other",
  ];

  const toggleDropdown = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY + 5, // Position below the button
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, 200),
    });
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setIsDropdownOpen(false);
    setShowModal(true); // Show the modal when an event type is selected
  };

  const closeModal = () => {
    setShowModal(false);
    setEventDetails({ date: "", description: "" });
    setBackgroundImage(null);
    setSelectedEvent(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEventDetails((prevDetails) => ({ ...prevDetails, [name]: value }));
  };

  const handleBackgroundChange = async (e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";

    if (!file) {
      setBackgroundImage(null);
      return;
    }

    setError(null);
    setIsPreparingBackground(true);

    try {
      const preparedFile = await prepareImageFileForUpload(file, {
        forceOptimization: true,
        maxBytes: 15 * 1024 * 1024,
      });
      setBackgroundImage(preparedFile);
    } catch (error) {
      console.error("Error preparing life event background:", error);
      setBackgroundImage(null);
      const message = getImagePreparationErrorMessage(error);
      setError(message);
      toast.error(message);
    } finally {
      setIsPreparingBackground(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) {
      return;
    }

    if (isPreparingBackground) {
      setError("Please wait for the background image to finish preparing.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("eventType", selectedEvent);
      formData.append("date", eventDetails.date);
      formData.append("description", eventDetails.description);

      if (backgroundImage) {
        formData.append("backgroundImage", backgroundImage);
      }

      const response = await axios.post(
        `${server}/lifeEvents/${profileType.toLowerCase()}/${ownerId}/life-events`,
        formData,
        { withCredentials: true }
      );
      if (response.data.success) {
        setLifeEvents((prevEvents) => [response.data.lifeEvent, ...prevEvents]);
        closeModal();
        toast.success("Life event created successfully!");
      } else {
        setError(response.data.message || "Failed to create life event.");
      }
    } catch (error) {
      console.error("Error creating life event:", error);
      setError(error.response?.data?.message || "Failed to create life event. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchLifeEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${server}/lifeEvents/${profileType.toLowerCase()}/${ownerId}/life-events`);
      if (response.data.success) {
        setLifeEvents(Array.isArray(response.data.lifeEvents) ? response.data.lifeEvents : []);
      } else {
        setError(response.data.message || "Failed to fetch life events.");
      }
    } catch (error) {
      console.error("Error fetching life events:", error);
      setError("Failed to fetch life events. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ownerId && profileType) {
      fetchLifeEvents();
    }
  }, [ownerId, profileType]);

  const activeLifeEvent = lifeEvents[0] || null;

  const activeLifeEventBackground = activeLifeEvent?.backgroundImage?.url;

  const activeLifeEventStyle = activeLifeEventBackground
    ? { backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.28), rgba(0, 0, 0, 0.58)), url(${activeLifeEventBackground})` }
    : undefined;

  const handleNewLifeEvent = (event) => {
    if (!canCreate) {
      return;
    }

    setIsActionMenuOpen(false);
    toggleDropdown(event);
  };

  const handleDeleteLifeEvent = async () => {
    if (!canCreate || !activeLifeEvent?._id) {
      return;
    }

    setLoading(true);
    setError(null);
    setIsActionMenuOpen(false);

    try {
      const response = await axios.delete(
        `${server}/lifeEvents/${profileType.toLowerCase()}/${ownerId}/life-events/${activeLifeEvent._id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        setLifeEvents((prevEvents) => prevEvents.filter((event) => event._id !== activeLifeEvent._id));
        toast.success("Life event deleted successfully!");
      } else {
        setError(response.data.message || "Failed to delete life event.");
      }
    } catch (error) {
      console.error("Error deleting life event:", error);
      setError("Failed to delete life event. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`life-events-component${canCreate ? ' life-events-component-can-create' : ' life-events-component-readonly'}`}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      {activeLifeEvent && canCreate ? (
        <div className="slot-action-menu life-event-slot-action-menu">
          <button
            type="button"
            className="slot-action-menu-button"
            aria-label="Life event actions"
            aria-expanded={isActionMenuOpen}
            onClick={() => setIsActionMenuOpen((isOpen) => !isOpen)}
          >
            &#8942;
          </button>
          {isActionMenuOpen && (
            <div className="slot-action-menu-list" role="menu">
              <button type="button" className="slot-action-menu-item" onClick={handleDeleteLifeEvent}>
                Delete Life Event
              </button>
              <button type="button" className="slot-action-menu-item" onClick={handleNewLifeEvent}>
                New Life Event
              </button>
            </div>
          )}
        </div>
      ) : (
        canCreate && !loading && (
          <button className="create-life-event-button" onClick={toggleDropdown}>
            Create a Life Event
            <span className={`arrow ${isDropdownOpen ? "up" : "down"}`}></span>
          </button>
        )
      )}

      {/* Render Dropdown as a Portal */}
      {canCreate && isDropdownOpen &&
        ReactDOM.createPortal(
          <ul
            className="life-events-dropdown"
            style={{
              position: "absolute",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
            }}
          >
            {lifeEventOptions.map((event, index) => (
              <li
                key={index}
                className="life-events-dropdown-item"
                onClick={() => handleEventSelect(event)}
              >
                {event}
              </li>
            ))}
          </ul>,
          document.body
        )}

      {/* Display Existing Life Events */}
      <div className="life-events-list">
        {loading && <div className="life-events-loading">Loading...</div>}
        {error && <div className="life-events-error">{error}</div>}
        {activeLifeEvent ? (
          <ul className="life-events-ul">
            <li
              key={activeLifeEvent._id}
              className="life-event-item"
              style={activeLifeEventStyle}
              aria-label={`${activeLifeEvent.eventType} life event`}
            >
              <p className="life-event-description">{activeLifeEvent.description}</p>
              <p className="life-event-date">{new Date(activeLifeEvent.date).toLocaleDateString()}</p>
            </li>
          </ul>
        ) : (
          !loading && <p className="life-events-no-events">No life events found.</p>
        )}
      </div>

      {/* Modal for Creating Life Event */}
      {canCreate && showModal &&
        ReactDOM.createPortal(
          <div className="life-events-modal-overlay" onClick={closeModal}>
            <div className="life-events-modal-content" onClick={(e) => e.stopPropagation()}>
              <span className="life-events-close-button" onClick={closeModal}>
                &times;
              </span>
              <h2>Create a {selectedEvent}</h2>
              <form onSubmit={handleSubmit} className="life-events-form">
                <label>
                  Date:
                  <input
                    type="date"
                    name="date"
                    value={eventDetails.date}
                    onChange={handleChange}
                    required
                  />
                </label>
                <label>
                  Description:
                  <textarea
                    name="description"
                    value={eventDetails.description}
                    onChange={handleChange}
                    required
                  ></textarea>
                </label>
                <label>
                  Background Image:
                  <input
                    type="file"
                    name="backgroundImage"
                    accept={IMAGE_ACCEPT_WITH_HEIC}
                    disabled={isPreparingBackground || loading}
                    onChange={handleBackgroundChange}
                  />
                </label>
                {isPreparingBackground && <p className="life-events-upload-status">Preparing background image...</p>}
                {backgroundImage && !isPreparingBackground && (
                  <p className="life-events-upload-status">Background image ready</p>
                )}
                <button type="submit" className="life-events-submit-button" disabled={loading || isPreparingBackground}>
                  {loading ? "Creating..." : "Create Event"}
                </button>
                {error && <p className="life-events-form-error">{error}</p>}
              </form>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default LifeEvents;


